/**
 * Utilidades para comunicarse con el LMS que embebe el LTI en un iframe.
 *
 * Solo tienen sentido en la vista Lti (components/Lti): la vista de administración
 * no se sirve embebida.
 *
 * Se exportan como funciones sueltas además de como mixin porque main.js también
 * las necesita y allí no hay componente del que colgar el mixin.
 */

/**
 * Determina si la aplicación se está sirviendo dentro de un iframe.
 */
export function isEmbeddedInFrame () {
  try {
    return window.self !== window.top
  } catch (e) {
    // Si el navegador nos deniega el acceso a window.top es porque estamos
    // dentro de un frame de otro dominio.
    return true
  }
}

/**
 * Hace scroll del documento padre hasta un rectángulo del iframe, expresado en
 * coordenadas de documento.
 *
 * No se llama a scrollIntoView sobre el elemento que interesa, sino sobre un
 * marcador invisible en flujo normal colocado en su sitio, por dos motivos
 * comprobados en Chrome:
 *
 *  - Sobre un elemento de un subárbol `position: fixed` (los b-sidebar y el overlay
 *    de vue-confirm-dialog lo son) no hace nada: el navegador lo considera siempre
 *    visible en su propio viewport y no propaga al frame padre.
 *  - Sobre `document.documentElement` tampoco es fiable, por ser él mismo el
 *    contenedor de scroll del documento.
 *
 * Sin `behavior: 'smooth'`: con desplazamiento suave la propagación al frame padre
 * no llega a producirse y la llamada no hace nada.
 */
export function scrollFrameToRect (left, top, width, height, block) {
  const marker = document.createElement('div')
  marker.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;' +
    'left:' + left + 'px;top:' + top + 'px;' +
    'width:' + width + 'px;height:' + height + 'px;'
  document.body.appendChild(marker)
  marker.scrollIntoView({block: block, inline: 'nearest'})
  document.body.removeChild(marker)
}

/**
 * Lleva el scroll del LMS al comienzo del iframe del LTI.
 *
 * Los paneles laterales (b-sidebar) y los modales se posicionan con
 * position: fixed respecto al viewport del iframe. Como el iframe se
 * redimensiona a la altura del contenido (ver changeHeightIFrame en main.js),
 * ese viewport es todo el iframe y no lo que el usuario está viendo: si ha
 * bajado por la página del LMS, el panel se abre en una zona no visible. Por
 * eso, al abrirlo, pedimos al LMS que suba hasta el comienzo del iframe.
 */
export function scrollLtiFrameToTop () {
  if (!isEmbeddedInFrame()) return
  // Mensaje de la especificación LTI postMessage que soportan Canvas y Moodle
  // con el plugin de compatibilidad, con el mismo formato de cadena JSON que
  // ya usa lti.frameResize en main.js.
  window.parent.postMessage(JSON.stringify({subject: 'lti.scrollToTop'}), '*')
  // Respaldo independiente del LMS, por si no atiende el mensaje.
  //
  // Con setTimeout y no con requestAnimationFrame: Chrome estrangula el rAF de los
  // iframes que quedan fuera de pantalla, que es justo la situación que estamos
  // corrigiendo, y la callback puede no llegar a ejecutarse nunca.
  window.setTimeout(() => {
    window.scrollTo(0, 0)
    scrollFrameToRect(0, 0, 1, 1, 'start')
  }, 0)
}

/**
 * Manejador del evento `change` de b-sidebar, que es el que emite al abrirse.
 * Ojo: b-sidebar NO emite `show`, solo `change`, `shown` y `hidden`.
 */
export function onLtiPanelVisibilityChange (visible) {
  if (visible) scrollLtiFrameToTop()
}

// Margen mínimo que se respeta con los bordes del iframe al anclar un cuadro al clic.
export const ANCHOR_MARGIN = 8

// Altura del último clic, en coordenadas del viewport del iframe. Solo interesa la
// vertical: en horizontal los cuadros se siguen centrando.
let lastAnchorY = null

function registerAnchor (event) {
  if (event.clientX || event.clientY) {
    // Clic de ratón: la altura exacta del puntero.
    lastAnchorY = event.clientY
    return
  }
  // Activación por teclado (Enter o Espacio sobre un elemento enfocado): el evento
  // llega con coordenadas a 0, así que usamos el centro del disparador.
  if (event.target && event.target.getBoundingClientRect) {
    const trigger = event.target.getBoundingClientRect()
    lastAnchorY = trigger.top + trigger.height / 2
  }
}

/**
 * Empieza a seguir la altura del último clic. Lo llama installAnchoredConfirm (main.js),
 * que es el único punto de arranque de todo esto.
 */
export function installClickAnchor () {
  // En captura, para registrar el clic aunque el handler del botón detenga la
  // propagación.
  document.addEventListener('click', registerAnchor, true)
}

export function getLastAnchorY () {
  return lastAnchorY
}

/**
 * Altura a la que hay que colocar un cuadro de `height` px para que quede centrado en
 * la altura del clic sin salirse de un contenedor de `containerHeight` px.
 */
export function anchoredTop (anchorY, height, containerHeight) {
  const min = ANCHOR_MARGIN
  const max = containerHeight - height - ANCHOR_MARGIN
  // Si el cuadro no cabe, max queda por debajo de min: mandamos el margen.
  if (max < min) return min
  return Math.min(Math.max(anchorY - height / 2, min), max)
}

/**
 * Coloca un b-modal a la altura del clic que lo ha abierto, en vez de centrado en el
 * iframe.
 *
 * Un modal `centered` se centra en el viewport del iframe, y como changeHeightIFrame
 * (main.js) redimensiona el iframe a la altura del contenido, ese viewport es todo el
 * iframe: en uno de 3000px el modal cae en y≈1500, casi siempre fuera de lo que el
 * usuario está mirando. Aquí no vale scrollLtiFrameToTop, que es lo que usan los
 * paneles: esos se pegan al top: 0 del iframe y el modal no.
 *
 * Ojo: el navegador NO nos saca de este apuro solo. BootstrapVue enfoca el modal al
 * abrirlo sin preventScroll, y ese scroll implícito llega al LMS únicamente cuando el
 * iframe va en otro proceso (LMS y LTI en sitios distintos, que es el caso de PRE y no
 * el de producción). Medido en Chrome el 2026-08-13.
 *
 * Solo actúa con el LTI embebido, así que la vista de administración —que también monta
 * el reproductor— se queda con el centrado de siempre.
 */
export function anchorLtiModalToClick (modalId) {
  const modal = document.getElementById(modalId)
  const dialog = modal && modal.querySelector('.modal-dialog')
  if (!dialog) return
  // Se parte siempre del centrado original, para no arrastrar la posición de una
  // apertura anterior si esta no se puede anclar.
  dialog.classList.remove('lti-anchored-dialog')
  dialog.style.marginTop = ''
  const anchorY = getLastAnchorY()
  if (!isEmbeddedInFrame() || anchorY === null) return

  // La clase va antes de medir: al desactivar el centrado por flexbox cambia la altura
  // resuelta del diálogo.
  dialog.classList.add('lti-anchored-dialog')
  dialog.style.marginTop = '0px'
  dialog.style.marginTop = anchoredTop(anchorY, dialog.getBoundingClientRect().height, modal.clientHeight) + 'px'

  // Empujón mínimo para que el modal se vea entero: 'nearest' no mueve nada si ya cabe
  // en la ventana y, si queda cortado por un borde, desplaza lo justo.
  const box = dialog.getBoundingClientRect()
  scrollFrameToRect(
    box.left + window.pageXOffset, box.top + window.pageYOffset,
    box.width, box.height, 'nearest'
  )
}

export default {
  methods: {
    isEmbeddedInFrame,
    scrollLtiFrameToTop,
    onLtiPanelVisibilityChange,
    anchorLtiModalToClick
  }
}



// WEBPACK FOOTER //
// ./src/mixins/ltiFrameMixin.js